import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Bell, CheckCircle2, XCircle, Clock } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/format";

const ACTION_BADGE = {
  accepted: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Accepted" },
  declined: { icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Declined" },
  expired: { icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "No response (expired)" },
};

export default function AdminCleanerResponses() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/cleaner-responses");
        setItems(unwrap(r)?.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-cleaner-responses">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">Cleaner responses</h1>
        <p className="text-[#4B5563] text-sm mt-1">Recent Accept / Decline actions from cleaners (and assignments that expired without a response), most recent first.</p>
      </div>

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {items.length === 0 ? (
          <div className="text-center py-16 text-[#4B5563]" data-testid="cleaner-responses-empty">
            <Bell className="w-7 h-7 mx-auto mb-2 text-[#9CA3AF]" />
            No cleaner responses yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cleaner-responses-table">
              <thead>
                <tr className="bg-[#FAFAF9] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-3 font-semibold">Cleaner</th>
                  <th className="px-4 py-3 font-semibold">Response</th>
                  <th className="px-4 py-3 font-semibold">Booking</th>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {items.map((r) => {
                  const meta = ACTION_BADGE[r.action] || ACTION_BADGE.accepted;
                  const Icon = meta.icon;
                  return (
                    <tr key={r.id} className="hover:bg-[#FAFAF9]" data-testid={`cleaner-response-${r.id}`}>
                      <td className="px-4 py-3 font-medium">{r.cleaner_name || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`${meta.cls} font-medium`}>
                          <Icon className="w-3 h-3 mr-1" /> {meta.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono">
                        <Link to={`/admin/bookings/${r.booking_id}`} className="text-[#0EA5A4] hover:underline" data-testid={`cleaner-response-booking-link-${r.id}`}>
                          #{r.booking_number}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{r.customer_full_name || "—"}</td>
                      <td className="px-4 py-3 text-[#4B5563]">{r.preferred_date || "—"} {r.preferred_time_slot ? `· ${r.preferred_time_slot}` : ""}</td>
                      <td className="px-4 py-3 text-xs text-[#9CA3AF] font-mono whitespace-nowrap">{formatDate(r.created_at, locale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
